import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function Contact({ settings }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  const s = settings || {};

  return (
    <section id="contact" className="py-24 bg-brand-mist">
      <div className="container-x grid lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <div className="overline text-brand-green">Contact</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-brand-ink leading-tight tracking-tight">
            Let's print something <span className="text-brand-blue">memorable.</span>
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Drop us a line, call, or come visit our studio in Bhopal. We typically respond within a few hours during business hours.
          </p>

          <ul className="mt-8 space-y-5">
            <li className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-brand-blue/10 grid place-items-center text-brand-blue shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="overline text-gray-500">Studio Address</div>
                <div className="font-medium text-brand-ink mt-1">{s.address}</div>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-brand-blue/10 grid place-items-center text-brand-blue shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="overline text-gray-500">Call Us</div>
                <a href={`tel:${s.phone}`} className="font-medium text-brand-ink mt-1 block hover:text-brand-blue">{s.phone}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-brand-blue/10 grid place-items-center text-brand-blue shrink-0">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="overline text-gray-500">Email</div>
                <a href={`mailto:${s.email}`} className="font-medium text-brand-ink mt-1 block hover:text-brand-blue">{s.email}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-brand-blue/10 grid place-items-center text-brand-blue shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="overline text-gray-500">Business Hours</div>
                <div className="font-medium text-brand-ink mt-1">{s.business_hours}</div>
              </div>
            </li>
            {s.website && (
              <li className="flex gap-4">
                <div className="h-11 w-11 rounded-xl bg-brand-blue/10 grid place-items-center text-brand-blue shrink-0">
                  <Globe className="h-5 w-5" />
                </div>
                <div>
                  <div className="overline text-gray-500">Website</div>
                  <div className="font-medium text-brand-ink mt-1 break-all">{s.website}</div>
                </div>
              </li>
            )}
          </ul>

          <a
            href={`https://wa.me/${(s.whatsapp || "").replace(/[^\d]/g, "")}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="contact-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-green text-white px-5 py-3 font-semibold hover:bg-brand-green-dark transition"
          >
            <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
          </a>

          {s.map_embed_url && (
            <div className="mt-8 rounded-3xl overflow-hidden border border-gray-200">
              <iframe
                title="Studio location"
                src={s.map_embed_url}
                className="w-full h-64"
                loading="lazy"
              />
            </div>
          )}
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-7 bg-white rounded-3xl border border-gray-200 p-8 lg:p-10 shadow-sm h-fit"
        >
          <h3 className="font-display text-2xl font-extrabold text-brand-ink">Request a Quote</h3>
          <p className="text-sm text-gray-500 mt-1">Fill in the form and we'll get back with a tailored quote.</p>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-gray-500">Name *</label>
                <input data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30" />
              </div>
              <div>
                <label className="overline text-gray-500">Phone *</label>
                <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline text-gray-500">Email *</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={update("email")} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30" />
              </div>
              <div>
                <label className="overline text-gray-500">Service</label>
                <input data-testid="contact-service" value={form.service} onChange={update("service")} placeholder="e.g. Banner Printing" className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30" />
              </div>
            </div>
            <div>
              <label className="overline text-gray-500">Message *</label>
              <textarea data-testid="contact-message" rows={5} value={form.message} onChange={update("message")} className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-green/30 resize-none" placeholder="Tell us about your project, sizes, quantity, deadline..." />
            </div>
            <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? "Sending..." : (<><Send className="h-4 w-4" /> Send Message</>)}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
